import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { ChatbotFacade } from '../../../facades/chatbot.facade';
import { ChatConversation } from '../../../domain/models/chat-conversation.model';
import { ConversationSummary } from '../../../domain/models/conversation-summary.model';
import { QuickQuestion } from '../../../domain/models/quick-question.model';
import { UserProfile } from '../../../domain/models/user-profile.model';
import { TALK_TO_YOUR_DATA_HISTORY_MAX_ITEMS } from '../../../constants/chatbot.constants';

const SEED_QUICK_QUESTIONS: readonly QuickQuestion[] = [
  {
    id: 'qq-1',
    title: 'Deliveries Volume',
    description: 'Review recent delivery metrics and trends',
    prompt: 'Show me the latest deliveries volume',
    category: 'operations',
  },
  {
    id: 'qq-2',
    title: 'Peak Performance',
    description: 'Analyze peak production performance periods',
    prompt: 'What are the peak performance periods?',
    category: 'performance',
  },
  {
    id: 'qq-3',
    title: 'Production Overview',
    description: 'Get a summary of current production status',
    prompt: 'Give me a production overview',
    category: 'production',
  },
  {
    id: 'qq-4',
    title: 'Plant Specifics',
    description: 'View details for a specific plant location',
    prompt: 'Show plant specific details',
    category: 'plant',
  },
];

@Component({
  selector: 'talk-to-your-data-chatbot-page',
  templateUrl: './chatbot-page.container.html',
  styleUrls: ['./chatbot-page.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatbotPageContainerComponent implements OnInit, OnDestroy {
  readonly activeConversation$: Observable<ChatConversation | null> = this.chatbotFacade.activeConversation$;
  readonly quickQuestions$: Observable<readonly QuickQuestion[]> = this.chatbotFacade.quickQuestions$;
  readonly conversationHistory$: Observable<readonly ConversationSummary[]> = this.chatbotFacade.conversationHistory$;
  readonly savedConversations$: Observable<readonly ConversationSummary[]> = this.chatbotFacade.savedConversations$;
  readonly selectedConversationId$: Observable<string | null> = this.chatbotFacade.selectedConversationId$;
  readonly sendingMessage$: Observable<boolean> = this.chatbotFacade.sendingMessage$;
  readonly loading$: Observable<boolean> = this.chatbotFacade.loading$;
  readonly error$: Observable<string | null> = this.chatbotFacade.error$;
  readonly userProfile$: Observable<UserProfile> = this.chatbotFacade.userProfile$;

  showSplash = true;
  isAnimatingOut = false;
  focusInputTrigger = 0;
  showConversationLimitModal = false;

  private exitTimeoutId?: ReturnType<typeof setTimeout>;
  private destroyTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly chatbotFacade: ChatbotFacade,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.chatbotFacade.initializeConversationState();
    this.chatbotFacade.setQuickQuestions(SEED_QUICK_QUESTIONS);
    this.chatbotFacade.loadConversationHistory();
    this.chatbotFacade.loadSavedConversations();
    this.exitTimeoutId = setTimeout(() => {
      this.isAnimatingOut = true;
      this.changeDetectorRef.markForCheck();

      this.destroyTimeoutId = setTimeout(() => {
        this.showSplash = false;
        this.changeDetectorRef.markForCheck();
      }, 800);
    }, 800);
  }

  ngOnDestroy(): void {
    clearTimeout(this.exitTimeoutId);
    clearTimeout(this.destroyTimeoutId);
  }

  onConversationSelected(conversationId: string): void {
    this.chatbotFacade.openConversation(conversationId);
  }

  onConversationSaved(conversationId: string): void {
    this.chatbotFacade.saveConversation(conversationId);
  }

  onConversationUnsaved(conversationId: string): void {
    this.chatbotFacade.unsaveConversation(conversationId);
  }

  onConversationRenamed(request: { conversationId: string; title: string }): void {
    this.chatbotFacade.renameConversation(request.conversationId, request.title);
  }

  onConversationDeleted(conversationId: string): void {
    this.chatbotFacade.deleteConversation(conversationId);
  }

  onNewChat(conversationHistory: readonly ConversationSummary[]): void {
    if (conversationHistory.length >= TALK_TO_YOUR_DATA_HISTORY_MAX_ITEMS) {
      this.showConversationLimitModal = true;
      return;
    }

    this.chatbotFacade.cancelAssistantRequest();
    this.chatbotFacade.setActiveConversation(null);
    this.chatbotFacade.selectConversation(null);
    this.focusInputTrigger++;
  }

  closeConversationLimitModal(): void {
    this.showConversationLimitModal = false;
  }

  onSuggestionSelected(question: QuickQuestion): void {
    this.chatbotFacade.sendQuickQuestion(question);
  }

  onMessageSubmitted(userMessage: string): void {
    this.chatbotFacade.sendMessage(userMessage);
  }

  onStopRequested(): void {
    this.chatbotFacade.cancelAssistantRequest();
    // this.chatbotFacade.setActiveConversation(null);
  }
}
